/**
 * Edit Session Modal Component
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { X } from 'lucide-react';

const EditSessionModal = ({ session, filterOptions, onSave, onClose }) => {
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (session) {
      setEditForm({
        sessionDate: session.session_date?.split('T')[0] || '',
        startedAt: session.started_at?.split('T')[1]?.substring(0, 5) || '09:00',
        endedAt: session.ended_at?.split('T')[1]?.substring(0, 5) || '09:15',
        durationMinutes: session.duration_minutes || 0,
        earnings: session.earnings || 0,
        status: session.status || 'completed',
        notes: session.notes || '',
        observerId: session.observer_id || '',
        childId: session.child_id || ''
      });
    }
  }, [session]);

  const handleSave = async () => {
    setSaving(true);
    const success = await onSave(session.id, editForm);
    setSaving(false);
    if (success) {
      onClose();
    }
  };

  if (!session) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Edit Session</CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Date</label>
              <Input
                type="date"
                value={editForm.sessionDate}
                onChange={(e) => setEditForm({...editForm, sessionDate: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Status</label>
              <select
                value={editForm.status}
                onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="completed">Completed</option>
                <option value="in_progress">In Progress</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Start Time</label>
              <Input
                type="time"
                value={editForm.startedAt}
                onChange={(e) => setEditForm({...editForm, startedAt: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm font-medium">End Time</label>
              <Input
                type="time"
                value={editForm.endedAt}
                onChange={(e) => setEditForm({...editForm, endedAt: e.target.value})}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Duration (minutes)</label>
              <Input
                type="number"
                value={editForm.durationMinutes}
                onChange={(e) => setEditForm({...editForm, durationMinutes: parseInt(e.target.value) || 0})}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Earnings (₹)</label>
              <Input
                type="number"
                value={editForm.earnings}
                onChange={(e) => setEditForm({...editForm, earnings: parseFloat(e.target.value) || 0})}
              />
            </div>
          </div>
          
          <div>
            <label className="text-sm font-medium">Observer</label>
            <select
              value={editForm.observerId}
              onChange={(e) => setEditForm({...editForm, observerId: e.target.value})}
              className="w-full border rounded-md px-3 py-2"
            >
              <option value="">Select Observer</option>
              {filterOptions.observers.map(obs => (
                <option key={obs.id} value={obs.id}>{obs.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="text-sm font-medium">Child</label>
            <select
              value={editForm.childId}
              onChange={(e) => setEditForm({...editForm, childId: e.target.value})}
              className="w-full border rounded-md px-3 py-2"
            >
              <option value="">Select Child</option>
              {filterOptions.children.map(child => (
                <option key={child.id} value={child.id}>{child.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="text-sm font-medium">Notes</label>
            <textarea
              value={editForm.notes}
              onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
              className="w-full border rounded-md px-3 py-2"
              rows={2}
              placeholder="Add notes about this correction..."
            />
          </div>
          
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EditSessionModal;
